/* eslint-disable react/no-unstable-nested-components */
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/16/solid';
import {
  ColumnDef,
  createColumnHelper,
  FilterFn,
  flexRender,
  getCoreRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  Row,
  RowData,
  useReactTable
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import CoverageIndicator from 'components/common/coverage-indicator';
import ProgressIndicator from 'components/common/progress-indicator';
import Tag from 'components/common/tag';
import AirHandlerCell from 'components/equipment-page/air-handler-cell';

import TableFilter from 'components/inputs/table-filter';
import ProductLink from 'components/products/product-link';
import {
  AirHandlerProductType,
  CondenserProductType,
  CondenserType,
  ValidSystemType
} from 'data/types';
import { setSystemsForNewOption } from 'features/systems';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DuctedCoverageType, getDuctedCoverage } from 'utils/ducted-coverage';
import { abbreviateNumber, formatUSD } from 'utils/helpers';
import { errorModal } from 'utils/ui';

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    filterVariant?: 'condenser' | 'air handler' | 'tags';
  }
  interface FilterFns {
    condenserFilter: FilterFn<unknown>;
    airHandlerFilter: FilterFn<unknown>;
    tagsFilter: FilterFn<unknown>;
  }
}

function CheckboxCell({
  systemId,
  isSelected,
  selectable,
  onSelect
}: {
  systemId: string;
  isSelected: boolean;
  selectable?: boolean;
  onSelect: (id: string) => void;
}) {
  if (!selectable) return <div />;

  return (
    <div className='px-2'>
      <input
        type='checkbox'
        className='h-5 w-5 cursor-pointer rounded border-2 border-gray-300 bg-white text-blue-600 focus:ring-blue-600'
        checked={isSelected}
        readOnly
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onSelect(systemId);
        }}
      />
    </div>
  );
}

function CondenserCell({
  product
}: {
  product: CondenserProductType | undefined;
}) {
  if (!product) {
    return <span className='text-red-500'>Unknown Product</span>;
  }

  return (
    <div className='flex flex-col'>
      <ProductLink type='condenser' product={product} hoverInfo />
      <div className='flex flex-wrap gap-1'>
        {product.coolingBTU && (
          <Tag color='default' text={abbreviateNumber(product.coolingBTU)} />
        )}
        <Tag color='default' text={`${product.seer} SEER`} />
      </div>
    </div>
  );
}

function PriceCell({ price }: { price: number }) {
  return <>{formatUSD(price)}</>;
}

function CoverageCell({
  data
}: {
  data: {
    coolingInsufficientTimePercent: number;
    heatingInsufficientTimePercent: number;
    minCoolingPercent: number;
    minHeatingPercent: number;
    coolingScore: number | null;
    heatingScore: number;
    ductedCoverage?: DuctedCoverageType;
  };
}) {
  // Ducted design-load coverage (AHA-6796): a row carrying ductedCoverage
  // renders the design-load chips ("—" for null), never the hours-weighted
  // ductless variant.
  if (data.ductedCoverage) {
    return (
      <div className='flex items-center gap-3'>
        <CoverageIndicator
          type='heating'
          ducted={{
            value: data.ductedCoverage.heatingCoveragePercent,
            basis: data.ductedCoverage.basis
          }}
        />
        <CoverageIndicator
          type='cooling'
          ducted={{
            value: data.ductedCoverage.coolingCoveragePercent,
            basis: data.ductedCoverage.basis
          }}
        />
      </div>
    );
  }

  return (
    <div className='flex items-center gap-3'>
      <CoverageIndicator
        type='heating'
        score={data.heatingScore}
        insufficientTimePercent={data.heatingInsufficientTimePercent}
        minimumPercent={data.minHeatingPercent}
      />
      <CoverageIndicator
        type='cooling'
        score={data.coolingScore}
        insufficientTimePercent={data.coolingInsufficientTimePercent}
        minimumPercent={data.minCoolingPercent}
      />
    </div>
  );
}

function StockCell({ inStock }: { inStock: boolean }) {
  return inStock ? (
    <div className='h-4 w-4 rounded-full bg-green-500' />
  ) : (
    <div className='h-4 w-4 rounded-full bg-red-500' />
  );
}

function TotalScoreCell({ total }: { total: number | null | undefined }) {
  if (total == null) return <span className='text-gray-400'>--</span>;
  return <span className='font-medium'>{Math.round(total)}</span>;
}

type SystemsTableProps = {
  condenser: CondenserType;
  condenserIndex?: number;
  totalCondensers?: number;
  selectable?: boolean;
  onSelectSystem?: (index: number) => void;
};

export default function SystemsTable({
  condenser,
  condenserIndex,
  totalCondensers,
  selectable,
  onSelectSystem
}: SystemsTableProps) {
  const dispatch = useAppDispatch();
  const { condenserProducts, airHandlerProducts } = useAppSelector(
    (state) => state.data
  );
  const { systemsForNewOption } = useAppSelector((state) => state.systems);
  const columnHelper = createColumnHelper<ValidSystemType>();
  const [data, setData] = useState<ValidSystemType[]>([]);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const filterUniqueSystems = (systems) => {
    const uniqueIds = new Set();
    return systems.filter((system) => {
      if (uniqueIds.has(system.id)) {
        return false;
      }
      uniqueIds.add(system.id);
      return true;
    });
  };

  const handleSelectSystem = useCallback(
    (systemId: string) => {
      const newSystems = [...systemsForNewOption];
      const condenserSystems = systemsForNewOption.filter(
        (s) => s.condenserUnique === condenser.uniqueId
      );
      const systemIds = condenser.validSystems.map((s) => s.id);
      if (!condenserSystems.map((s) => s.id).includes(systemId)) {
        systemIds.forEach((id) => {
          const oldSystemIndex = systemsForNewOption.findIndex(
            (s) => s.condenserUnique === condenser.uniqueId && s.id === id
          );
          if (oldSystemIndex !== -1) {
            newSystems.splice(oldSystemIndex, 1);
          }
        });
        const selectedSystem = structuredClone(
          condenser.validSystems.find((s) => s.id === systemId)
        );
        if (selectedSystem) {
          newSystems.push(selectedSystem as ValidSystemType);
          dispatch(setSystemsForNewOption(newSystems));
          if (
            condenserIndex !== undefined &&
            totalCondensers !== undefined &&
            onSelectSystem
          ) {
            const nextCondenserIndex = condenserIndex + 1;
            if (nextCondenserIndex < totalCondensers) {
              onSelectSystem(nextCondenserIndex);
            }
          }
        } else {
          errorModal('Selected system not found');
        }
      } else {
        dispatch(
          setSystemsForNewOption(
            newSystems.filter(
              (s) =>
                s.condenserUnique !== condenser.uniqueId || s.id !== systemId
            )
          )
        );
      }
    },
    [
      systemsForNewOption,
      condenser.uniqueId,
      condenser.validSystems,
      dispatch,
      condenserIndex,
      totalCondensers,
      onSelectSystem
    ]
  );

  const columns = useMemo<ColumnDef<ValidSystemType, any>[]>(
    () => [
      columnHelper.accessor('id', {
        header: '',
        size: 50,
        cell: (info) => (
          <CheckboxCell
            selectable={selectable}
            systemId={info.getValue()}
            isSelected={systemsForNewOption
              .filter((s) => s.condenserUnique === condenser.uniqueId)
              .map((s) => s.id)
              .includes(info.getValue())}
            onSelect={handleSelectSystem}
          />
        ),
        enableColumnFilter: false,
        enableSorting: false
      }),
      columnHelper.accessor(
        (system) =>
          condenserProducts.find(
            (cp) => cp.productId === system.condenserProductId
          ),
        {
          header: 'Condenser',
          size: 200,
          cell: (info) => (
            <CondenserCell product={info.getValue() as CondenserProductType} />
          ),
          meta: {
            filterVariant: 'condenser'
          },
          filterFn: 'condenserFilter',
          enableColumnFilter: true,
          enableSorting: false
        }
      ),
      columnHelper.accessor(
        (system) =>
          airHandlerProducts.find(
            (ah) => ah.productId === system.airHandler1ProductId
          ),
        {
          header: 'Zone 1',
          size: 200,
          cell: (info) => <AirHandlerCell airHandler={info.getValue()} />,
          meta: {
            filterVariant: 'air handler'
          },
          filterFn: 'airHandlerFilter',
          enableColumnFilter: true,
          enableSorting: false
        }
      ),
      columnHelper.accessor(
        (system) =>
          airHandlerProducts.find(
            (ah) => ah.productId === system.airHandler2ProductId
          ),
        {
          id: 'zone2',
          header: 'Zone 2',
          size: 200,
          cell: (info) => <AirHandlerCell airHandler={info.getValue()} />,
          meta: {
            filterVariant: 'air handler'
          },
          filterFn: 'airHandlerFilter',
          enableColumnFilter: true,
          enableSorting: false
        }
      ),
      columnHelper.accessor(
        (system) =>
          airHandlerProducts.find(
            (ah) => ah.productId === system.airHandler3ProductId
          ),
        {
          id: 'zone3',
          header: 'Zone 3',
          size: 200,
          cell: (info) => <AirHandlerCell airHandler={info.getValue()} />,
          meta: {
            filterVariant: 'air handler'
          },
          filterFn: 'airHandlerFilter',
          enableColumnFilter: true,
          enableSorting: false
        }
      ),
      columnHelper.accessor(
        (system) =>
          airHandlerProducts.find(
            (ah) => ah.productId === system.airHandler4ProductId
          ),
        {
          id: 'zone4',
          header: 'Zone 4',
          size: 200,
          cell: (info) => <AirHandlerCell airHandler={info.getValue()} />,
          meta: {
            filterVariant: 'air handler'
          },
          filterFn: 'airHandlerFilter',
          enableColumnFilter: true,
          enableSorting: false
        }
      ),
      columnHelper.accessor(
        (system) =>
          airHandlerProducts.find(
            (ah) => ah.productId === system.airHandler5ProductId
          ),
        {
          id: 'zone5',
          header: 'Zone 5',
          size: 200,
          cell: (info) => <AirHandlerCell airHandler={info.getValue()} />,
          meta: {
            filterVariant: 'air handler'
          },
          filterFn: 'airHandlerFilter',
          enableColumnFilter: true,
          enableSorting: false
        }
      ),
      columnHelper.accessor('baseRetailPrice', {
        header: 'Price',
        size: 80,
        cell: (info) => <PriceCell price={info.getValue()} />,
        enableColumnFilter: false,
        enableSorting: true,
        sortDescFirst: false
      }),
      columnHelper.accessor(
        (system) => ({
          coolingInsufficientTimePercent:
            system.percentageOfTimeOfInsufficientCooling ?? 0,
          heatingInsufficientTimePercent:
            system.percentageOfTimeOfInsufficientHeating ?? 0,
          minCoolingPercent: system.avgPartialCoolingCoveragePercent ?? 0,
          minHeatingPercent: system.avgPartialHeatingCoveragePercent ?? 0,
          coolingScore: system.coolingScore ?? null,
          heatingScore: system.heatingScore ?? 0,
          ductedCoverage: getDuctedCoverage(system)
        }),
        {
          header: 'Coverage',
          cell: (info) => <CoverageCell data={info.getValue()} />,
          enableColumnFilter: false,
          enableSorting: true,
          sortingFn: (rowA, rowB) =>
            (rowB.original.heatingScore ?? 0) +
            (rowB.original.coolingScore ?? 0) -
            ((rowA.original.heatingScore ?? 0) +
              (rowA.original.coolingScore ?? 0)),
          sortDescFirst: false
        }
      ),
      columnHelper.accessor('inStock', {
        header: 'Stock',
        size: 60,
        cell: (info) => <StockCell inStock={info.getValue()} />,
        enableColumnFilter: false,
        enableSorting: false
      }),
      columnHelper.accessor((system) => system.score?.total ?? null, {
        id: 'totalScore',
        header: 'Total',
        size: 80,
        cell: (info) => <TotalScoreCell total={info.getValue()} />,
        enableColumnFilter: false,
        enableSorting: true,
        sortDescFirst: true,
        sortingFn: (rowA, rowB) =>
          (rowA.original.score?.total ?? 0) - (rowB.original.score?.total ?? 0)
      })
    ],
    [
      selectable,
      systemsForNewOption,
      condenser.uniqueId,
      condenserProducts,
      airHandlerProducts,
      handleSelectSystem
    ]
  );

  const condenserFilter: FilterFn<ValidSystemType> = (
    row: Row<ValidSystemType>,
    columnId: string,
    filterValue: string[]
  ): boolean => {
    const product = row.getValue(columnId) as CondenserProductType;
    let matchesFilter = true;
    if (
      filterValue[0] &&
      !product.model.toLowerCase().includes(filterValue[0]?.toLowerCase())
    ) {
      matchesFilter = false;
    }
    if (filterValue[1] && product.coolingBTU.toString() !== filterValue[1]) {
      matchesFilter = false;
    }
    return matchesFilter;
  };

  const airHandlerFilter: FilterFn<ValidSystemType> = (
    row: Row<ValidSystemType>,
    columnId: string,
    filterValue: string[]
  ): boolean => {
    const product = row.getValue(columnId) as AirHandlerProductType;
    let matchesFilter = true;
    if (
      filterValue[0] &&
      !product.model.toLowerCase().includes(filterValue[0]?.toLowerCase()) &&
      !product.type.toLowerCase().includes(filterValue[0]?.toLowerCase())
    ) {
      matchesFilter = false;
    }
    if (filterValue[1] && product.sizeCode !== filterValue[1]) {
      matchesFilter = false;
    }
    return matchesFilter;
  };

  const tagsFilter: FilterFn<ValidSystemType> = (
    row: Row<ValidSystemType>,
    columnId: string,
    filterValue: string[]
  ): boolean => {
    const tags = (row.getValue(columnId) as string[]) ?? [];
    let matchesFilter = true;
    if (
      filterValue.length > 0 &&
      !filterValue.every((tag) => tags.includes(tag))
    ) {
      matchesFilter = false;
    }
    return matchesFilter;
  };

  const table = useReactTable({
    data,
    columns,
    state: {
      columnVisibility: {
        zone2: condenser.validSystems[0]?.airHandler2ProductId != null,
        zone3: condenser.validSystems[0]?.airHandler3ProductId != null,
        zone4: condenser.validSystems[0]?.airHandler4ProductId != null,
        zone5: condenser.validSystems[0]?.airHandler5ProductId != null
      }
    },
    filterFns: {
      condenserFilter,
      airHandlerFilter,
      tagsFilter
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    enableRowSelection: true,
    initialState: {
      sorting: [{ id: 'totalScore', desc: true }]
    }
  });

  const { rows } = table.getRowModel();

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: () => 26,
    getScrollElement: () => tableContainerRef.current,
    measureElement:
      typeof window !== 'undefined' &&
      navigator.userAgent.indexOf('Firefox') === -1
        ? (element) => element?.getBoundingClientRect().height
        : undefined,
    overscan: 5
  });

  useEffect(() => {
    setData(filterUniqueSystems(condenser.validSystems));
  }, [condenser.validSystems]);

  return (
    <div className='flex flex-col gap-3'>
      <div className='flex items-center gap-2'>
        {selectable && (
          <ProgressIndicator
            required
            checked={systemsForNewOption.some(
              (s) => s.condenserUnique === condenser.uniqueId
            )}
          />
        )}
        <div className='text-xl font-bold text-gray-900'>
          {condenser.name} ({table.getFilteredRowModel().rows.length} systems)
        </div>
      </div>
      <div
        ref={tableContainerRef}
        className='relative max-h-[500px] overflow-auto rounded-lg bg-white shadow ring-1 ring-black ring-opacity-5'
      >
        <table className='grid table-fixed border-collapse'>
          <thead className='sticky top-0 z-10 grid border-b border-gray-300 bg-gray-50'>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className='flex w-full text-left'>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className='flex flex-col gap-1 px-3 py-3.5 text-left text-sm font-semibold text-gray-900'
                    style={{ width: header.getSize() }}
                  >
                    <button
                      type='button'
                      className={`group flex items-center ${
                        header.column.getCanSort()
                          ? 'cursor-pointer'
                          : 'cursor-default'
                      }`}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                      {header.column.getIsSorted() === 'asc' && (
                        <ChevronDownIcon className='h-5 w-5' />
                      )}
                      {header.column.getIsSorted() === 'desc' && (
                        <ChevronUpIcon className='h-5 w-5' />
                      )}
                      {header.column.getCanSort() &&
                        header.column.getIsSorted() === false && (
                          <ChevronDownIcon className='h-5 w-5 text-transparent group-hover:text-gray-400' />
                        )}
                    </button>
                    {header.column.getCanFilter() ? (
                      <div className='font-normal'>
                        <TableFilter column={header.column} />
                      </div>
                    ) : null}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody
            className='relative grid bg-white'
            style={{
              height:
                rowVirtualizer.getVirtualItems().length > 0
                  ? `${rowVirtualizer.getTotalSize()}px`
                  : 'auto'
            }}
          >
            {rowVirtualizer.getVirtualItems().length > 0 ? (
              rowVirtualizer.getVirtualItems().map((virtualRow) => {
                const row = rows[virtualRow.index];
                const system = row.original;
                const isSelected = systemsForNewOption
                  .filter((s) => s.condenserUnique === condenser.uniqueId)
                  .map((s) => s.id)
                  .includes(system.id);
                return (
                  <tr
                    className={`absolute flex w-full items-center border-b border-gray-300 ${
                      isSelected && 'bg-blue-50'
                    } ${!isSelected && selectable && 'hover:bg-gray-50'} ${
                      selectable && 'cursor-pointer'
                    }`}
                    data-index={virtualRow.index}
                    ref={(node) => rowVirtualizer.measureElement(node)}
                    key={row.id}
                    style={{ transform: `translateY(${virtualRow.start}px)` }}
                    onClick={
                      selectable
                        ? () => handleSelectSystem(system.id)
                        : undefined
                    }
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        style={{ width: cell.column.getSize() }}
                        className='flex px-3 py-4 text-sm text-gray-900'
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  className='flex h-20 items-center justify-center'
                  colSpan={
                    table.getAllColumns().filter((col) => col.getIsVisible())
                      .length
                  }
                >
                  <span className='text-gray-500'>No systems found.</span>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
